import { useApolloClient } from "@apollo/client/react";
import { Button, Css, Palette, useComputed, useModal, useSnackbar, useTestIds } from "@homebound/beam";
import { createObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { Icon } from "src/components";
import { BidPackageDetailPageDocument } from "src/routes/bids/BidPackageDetailPage.generated";
import { useSaveBidPackageMutation } from "src/routes/bids/components/EditBidPackageModal.generated";
import {
  ConfigureBidGroupsStepDocument,
  useConfigureBidGroupsStepLazyQuery,
} from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { PlanBasedBidPackageReviewAndDownloadStepDocument } from "src/routes/bids/plan-based-bid-package/components/3.ReviewAndDownloadStep.generated";
import { AddOptionModal } from "src/routes/bids/plan-based-bid-package/components/AddOptionModal";
import {
  mapToBidPlanGroupsInput,
  planBasedBidPackageFormConfig,
  PlanBasedBidPackageWizard,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";
import { createProductOfferingScopeUrl } from "src/RouteUrls";
import { count } from "src/utils";
import { openNewTab } from "src/utils/window";
import {
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupsDocument,
  PlanBasedBidsTableV2_MissingBidPlanOptionFragment,
} from "./PlanBasedBidsTableV2.generated";

type MissingOptionsCardProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  /** Set when the card renders inside the create/edit wizard, whose form owns the exhaustive `bidPlanGroups` list. */
  wizard?: PlanBasedBidPackageWizard;
};

export const MissingOptionsCard = observer(function MissingOptionsCard(props: MissingOptionsCardProps) {
  const { bidPackage, wizard } = props;
  const tid = useTestIds({});
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const client = useApolloClient();
  const [loadConfig] = useConfigureBidGroupsStepLazyQuery();
  const [saveBidPackage] = useSaveBidPackageMutation({
    refetchQueries: [
      BidPackageDetailPageDocument,
      PlanBasedBidsTableV2_BidPlanChildGroupsDocument,
      ConfigureBidGroupsStepDocument,
    ],
  });
  // `optionsMissingFromPackage` reflects persisted state, which lags the wizard's form right after
  // "Add to Bid" (until the refetch lands) and when arriving with unsaved edits. the form is what
  // the next save persists, so an option it already holds is not missing.
  const stagedOptionIds = useComputed(
    () =>
      (wizard?.formState.bidPlanGroups.value ?? [])
        .filter((g) => !g.archive)
        .map((g) => g.globalOptionId)
        .compact(),
    [wizard?.formState],
  );
  const missingOptions = bidPackage.optionsMissingFromPackage.filter(
    (o) => !stagedOptionIds.includes(o.globalOption.id),
  );
  if (missingOptions.isEmpty) return null;

  async function handleAddOptions(options: PlanBasedBidsTableV2_MissingBidPlanOptionFragment[]) {
    const { data } = await loadConfig({
      variables: { bidPackageId: bidPackage.id, productOfferingIds: bidPackage.productOfferings.map((pof) => pof.id) },
    });
    if (!data) return;
    // Outside the wizard there's no form to add to, so seed one from the package's current groups
    // so AddOptionModal appends the new option to the full list.
    const formState =
      wizard?.formState ??
      createObjectState(planBasedBidPackageFormConfig, {
        bidPackageId: bidPackage.id,
        bidPlanGroups: data.bidPackage.bidPlanGroups.map((bpg) => ({
          id: bpg.id,
          isNoCost: bpg.isNoCost,
          globalOptionId: bpg.globalOption?.id,
          primaryOptionGroupId: bpg.primaryOptionGroup?.id,
          order: bpg.order,
          dimensions: bpg.dimensions.map((d) => ({
            id: d.id,
            globalOptionTypeId: d.globalOptionType?.id,
            globalOptionGroupId: d.globalOptionGroup?.id,
            globalOptionIds: d.globalOptions.map((go) => go.id),
          })),
        })),
      });
    const addedLabel = options.length === 1 ? options[0].globalOption.name : count(options, "option");
    openModal({
      content: (
        <AddOptionModal
          formState={formState}
          dimensionResults={data.potentialBidPlanDimensions}
          potentialBidPlanOptions={data.bidPackage.potentialBidPlanOptions}
          defaultSelectedOptionIds={options.map((o) => o.globalOption.id)}
          onSaved={async () => {
            if (wizard) {
              await wizard.save();
              // The wizard's mutation only returns the groups, so pull the rest of the review data.
              await client.refetchQueries({
                include: [
                  PlanBasedBidsTableV2_BidPlanChildGroupsDocument,
                  PlanBasedBidPackageReviewAndDownloadStepDocument,
                ],
              });
            } else {
              await saveBidPackage({
                variables: {
                  input: {
                    id: bidPackage.id,
                    bidPlanGroups: mapToBidPlanGroupsInput(formState.bidPlanGroups.value ?? []),
                  },
                },
              });
            }
            triggerNotice({ message: `${addedLabel} added to the bid.` });
          }}
        />
      ),
    });
  }

  async function handleMarkAsNoCost(missingOption: PlanBasedBidsTableV2_MissingBidPlanOptionFragment) {
    const { data } = await loadConfig({
      variables: { bidPackageId: bidPackage.id, productOfferingIds: bidPackage.productOfferings.map((pof) => pof.id) },
    });
    if (!data) return;
    const updatedGroups = renumberGroups([
      { globalOptionId: missingOption.globalOption.id, isNoCost: true, order: 0, dimensions: [] },
      ...data.bidPackage.bidPlanGroups.map((g) => ({ id: g.id, globalOptionId: g.globalOption?.id, order: g.order })),
    ]);
    await saveBidPackage({ variables: { input: { id: bidPackage.id, bidPlanGroups: updatedGroups } } });
    triggerNotice({ message: `${missingOption.globalOption.name} marked as no cost.` });
  }

  return (
    <div {...tid.card} css={Css.ba.bcOrange700.bw2.br12.bgWhite.mt1.mb3.$}>
      <div css={Css.df.gap1.p2.bb.bcGray200.$}>
        <div>
          <Icon icon="error" color={Palette.Orange700} />
        </div>
        <div css={Css.w100.df.fdc.$}>
          <span css={Css.smSb.pb1.gray900.$}>Options Missing From Bid Package</span>
          <span css={Css.sm.gray700.$}>The options listed below contain scope for this cost code.</span>
        </div>
        <div css={Css.asc.$}>
          <Button variant="secondary" icon="plus" label="Add All" onClick={() => handleAddOptions(missingOptions)} />
        </div>
      </div>
      {missingOptions.map((option) => (
        <div key={option.globalOption.id} {...tid.row} css={Css.df.aic.jcsb.gap2.p2.bt.bcOrange700.$}>
          <div css={Css.df.fdc.pl4.$}>
            <span css={Css.smSb.gray900.pb1.$}>{option.globalOption.name}</span>
            <div css={Css.xs.gray700.$}>
              Scope detected in{" "}
              {option.productOfferings.map((pof, i) => {
                const rpav = pof.aggregateActive;
                return (
                  <span key={pof.id}>
                    {i > 0 && ", "}
                    {rpav ? (
                      <span
                        css={Css.xsSb.blue700.cursorPointer.$}
                        onClick={() =>
                          openNewTab(
                            createProductOfferingScopeUrl(pof.id, rpav.id, {
                              filter: { costCode: bidPackage.costCodes.map((cc) => cc.id) },
                            }),
                          )
                        }
                      >
                        {pof.displayName} v{rpav.version}
                      </span>
                    ) : (
                      pof.displayName
                    )}
                  </span>
                );
              })}
            </div>
          </div>

          <div css={Css.df.aic.gap2.$}>
            <Button variant="text" label="Mark as No Cost" onClick={() => handleMarkAsNoCost(option)} />
            <Button variant="secondary" label="Add to Bid" onClick={() => handleAddOptions([option])} />
          </div>
        </div>
      ))}
    </div>
  );
});
